
import { Component, OnDestroy, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Data, Event, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { LoggedOutModalComponent } from '@shared-angular/components/logged-out-modal/logged-out-modal.component';
import { LanguageService } from '@shared-angular/services/language.service';
import { LoginService } from '@shared-angular/services/login.service';
import { ChatbotConfig, ServiceStatus } from '@shared/index';
import dayjs from 'dayjs';
import locale_de from 'dayjs/locale/de';
import CustomParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { ProjectService } from './shared/services/project.service';
import { RouteShareService } from './shared/services/route-share.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, FormsModule],
  providers: [BsModalService]
})
export class AppComponent implements OnDestroy {
  public title = 'ki-toolbox-app';
  //public route: string = "";
  public status: ServiceStatus = "waiting";

  private readonly _isFullScreenLayout = signal(false);
  public get isFullScreenLayout(): boolean { return this._isFullScreenLayout(); }
  public set isFullScreenLayout(value: boolean) { this._isFullScreenLayout.set(value); }
  public subscriptions: Subscription[] = [];




  constructor(public router: Router,
    public loginService: LoginService,
    private langService: LanguageService,
    private modalService: BsModalService,
    private rss: RouteShareService,
    private projectService: ProjectService,
    private titleService: Title
  ) {
    if (this.langService.getLanguage() === "de") {
      dayjs.locale(locale_de);
      dayjs.extend(updateLocale);
      dayjs.updateLocale('de', {
        calendar: {
          lastDay: '[Gestern um] HH:mm',
          sameDay: '[Heute um] HH:mm',
          nextDay: '[Morgen um] HH:mm',
          lastWeek: '[Letzten] dddd [um] HH:mm',
          nextWeek: '[Nächsten] dddd [um] HH:mm',
          sameElse: 'DD.MM.YYYY'
        },
        relativeTime: {
          future: "in %s",
          past: "vor %s",
          s: 'ein paar Sekunden',
          m: "einer Minute",
          mm: "%d Minuten",
          h: "einer Stunde",
          hh: "%d Stunden",
          d: "einem Tag",
          dd: "%d Tagen",
          M: "einem Monat",
          MM: "%d Monaten",
          y: "einem Jahr",
          yy: "%d Jahren"
        }
      });
      dayjs.extend(CustomParseFormat);
    }
    dayjs.extend(relativeTime);
    dayjs.extend(duration);

    this.subscriptions.push(router.events.subscribe((e: Event) => {
      if (e instanceof NavigationEnd) {
        const routeData: Data = this.getRouteData(this.router.routerState.root);
        this.isFullScreenLayout = !!routeData['isFullScreenLayout'];
        this.updateDocumentTitle();
      }
    }));

    this.loginService.socketAdmin.onLogoutOldSession.subscribe(_los => {
      this.modalService.show(LoggedOutModalComponent);
    });
  }

  private getRouteData(route: ActivatedRoute) {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot.data;
  }

  private getAllRouteParams(route: ActivatedRoute): Record<string, string> {
    let params: Record<string, string> = { ...route.snapshot.params };
    if (route.firstChild) {
      params = { ...params, ...this.getAllRouteParams(route.firstChild) };
    }
    return params;
  }

  private updateDocumentTitle(): void {
    const params = this.getAllRouteParams(this.router.routerState.root);
    if (params['id']) {
      // botConfig ist beim ersten Routing evtl. noch nicht geladen
      const botConfig = this.rss.botConfig as ChatbotConfig | undefined;
      this.titleService.setTitle('reflact AI-Engine: ' + (botConfig?.name ?? params['id']));
    } else if (params['projectid']) {
      const pid = params['projectid'];
      if (pid === '-') {
        this.titleService.setTitle('reflact AI-Engine: ' + $localize`:@@my-bots:Meine Bots`);
      } else {
        const project = this.projectService.projects.find(p => p._id === pid);
        this.titleService.setTitle('reflact AI-Engine: ' + (project?.name ?? pid));
      }
    } else {
      this.titleService.setTitle('reflact AI-Engine');
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(s => { s.unsubscribe(); });
  }
}
